import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

export type PropertySummary = {
  id: string;
  name: string;
  /** Application kind. 'web' = browser app; 'mcp' = MCP server; 'api' reserved. */
  type?: 'web' | 'api' | 'mcp';
  baseUrl?: string | null;
  description?: string | null;
};

export type CredentialSummary = {
  id: string;
  name: string;
  environmentId?: string | null;
  templateId?: string | null;
  lastLoginSucceededAt?: string | null;
  storageStateS3Key?: string | null;
  /** True for the property's default credential — always present on the wire (`PropertyCredentialMetadata.isDefault`). */
  isDefault: boolean;
};

type OrgCredentialSummary = {
  id: string;
  name: string;
  propertyId: string;
};

const fetchPropertySummaries = async (): Promise<PropertySummary[]> => {
  const response = await fetch(`${API_URL}/org/properties`, { credentials: 'include' });
  if (!response.ok) {
    throw await toApiError(response, 'Failed to fetch applications');
  }
  const data = (await response.json()) as { items?: PropertySummary[] };
  return Array.isArray(data.items) ? data.items : [];
};

export const usePropertySummaries = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  return useQuery({
    queryKey: ['org-properties', 'summaries'],
    queryFn: fetchPropertySummaries,
    enabled,
    staleTime: 60_000,
  });
};

const fetchCredentialSummaries = async (
  propertyId: string
): Promise<{ items: CredentialSummary[]; accessDenied: boolean }> => {
  const response = await fetch(`${API_URL}/org/properties/${propertyId}/credentials`, {
    credentials: 'include',
  });
  if (response.status === 403) {
    return { items: [], accessDenied: true };
  }
  if (!response.ok) {
    throw await toApiError(response, 'Failed to fetch credentials');
  }
  const data = (await response.json()) as { items?: CredentialSummary[] };
  return {
    items: Array.isArray(data.items) ? data.items : [],
    accessDenied: false,
  };
};

export const usePropertyCredentialSummaries = (
  propertyId: string | null | undefined,
  options?: { enabled?: boolean }
) => {
  const enabled = options?.enabled ?? true;
  return useQuery({
    queryKey: ['property-credentials', propertyId],
    queryFn: () => fetchCredentialSummaries(propertyId!),
    enabled: enabled && Boolean(propertyId),
    staleTime: 30_000,
  });
};

const fetchOrgCredentials = async (): Promise<{
  items: OrgCredentialSummary[];
  accessDenied: boolean;
}> => {
  const response = await fetch(`${API_URL}/org/credentials`, {
    credentials: 'include',
  });
  if (response.status === 403) {
    return { items: [], accessDenied: true };
  }
  if (!response.ok) {
    throw await toApiError(response, 'Failed to fetch org credentials');
  }
  const data = (await response.json()) as { items?: OrgCredentialSummary[] };
  return {
    items: Array.isArray(data.items) ? data.items : [],
    accessDenied: false,
  };
};

export const useOrgCredentials = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  return useQuery({
    queryKey: ['org-credentials'],
    queryFn: fetchOrgCredentials,
    enabled,
    staleTime: 30_000,
  });
};
